import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/Button";
import { Container } from "../ui/Container";
import { ThemeToggle } from "../ThemeToggle";
import { navItems } from "../../lib/siteContent";
import { useSiteContent } from "../../lib/content";
import { cn } from "../../lib/utils";

export function Header() {
  const { content } = useSiteContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b transition duration-300",
        scrolled
          ? "border-white/10 bg-background/80 backdrop-blur-md light:border-black/10"
          : "border-transparent bg-transparent",
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-4 md:h-[4.25rem]">
        <a href="#inicio" className="flex items-center gap-2 font-heading text-lg font-semibold text-primary">
          <img
            src="/icon.png"
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 rounded-md"
          />
          {content.contact.name}
        </a>

        <nav className="hidden items-center gap-7 md:flex" aria-label="Secções">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm text-muted transition hover:text-primary"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <ThemeToggle />
          <Button href="#contacto" size="sm">
            {content.hero.ctaPrimary}
          </Button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border bg-surface text-primary"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {open && (
          <>
            <motion.button
              type="button"
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              aria-label="Fechar menu"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="fixed inset-y-0 right-0 z-50 flex w-[min(18rem,86vw)] flex-col border-l bg-surface p-6 shadow-glow md:hidden"
              initial={reduce ? false : { x: "100%" }}
              animate={{ x: 0 }}
              exit={reduce ? undefined : { x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              role="dialog"
              aria-modal="true"
              aria-label="Menu de navegação"
            >
            <div className="mb-8 flex items-center justify-between">
              <span className="font-heading font-semibold text-primary">Menu</span>
              <button
                type="button"
                className="rounded-lg p-2 text-muted hover:text-primary"
                aria-label="Fechar menu"
                onClick={() => setOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-4" aria-label="Secções móveis">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-lg text-primary"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-8 flex items-center gap-3">
              <ThemeToggle />
              <Button href="#contacto" className="flex-1" onClick={() => setOpen(false)}>
                {content.hero.ctaPrimary}
              </Button>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
